import { HttpError } from '@/lib/httpError'

interface User {
  id: string
  account: string
  name: string
  platform_role: 'owner' | 'admin' | 'auditor' | 'security' | 'user'
  type: 'local' | 'ldap' | 'oidc'
  created_at: string
  updated_at: string
  last_login_at?: string
  external_id?: string
  totp_enabled?: boolean
  contract_agreement?: boolean
}

interface ListUsersResponse {
  users: User[]
  total: number
  limit: number
  offset: number
}

interface ListUsersParams {
  limit?: number
  offset?: number
  q?: string
}

interface CreateUserRequest {
  account: string
  name: string
  password: string
  role?: 'owner' | 'admin' | 'auditor' | 'security' | 'user'
}

class UserService {
  async getUsers(params?: ListUsersParams): Promise<ListUsersResponse> {
    const queryParams = new URLSearchParams()

    if (params?.limit !== undefined) {
      queryParams.append('limit', params.limit.toString())
    }
    if (params?.offset !== undefined) {
      queryParams.append('offset', params.offset.toString())
    }
    if (params?.q) {
      queryParams.append('q', params.q)
    }

    const endpoint = `/api/v2/users${queryParams.toString() ? `?${queryParams}` : ''}`
    const response = await fetch(endpoint, {
      method: 'GET',
      credentials: 'include',
    })

    if (!response.ok) {
      switch (response.status) {
        case 401:
          throw new HttpError('errors.unauthorized', 401)
        case 403:
          throw new HttpError('errors.forbidden', 403)
        case 500:
          throw new HttpError('errors.serverError', 500)
        default:
          throw new HttpError('errors.getUsersFailed', response.status)
      }
    }

    const data: ListUsersResponse = await response.json()
    return data
  }

  async getUserById(id: string): Promise<User> {
    const response = await fetch(`/api/v2/users/${id}`, {
      method: 'GET',
      credentials: 'include',
    })

    if (!response.ok) {
      switch (response.status) {
        case 401:
          throw new Error('errors.unauthorized')
        case 403:
          throw new Error('errors.forbidden')
        case 404:
          throw new Error('errors.userNotFound')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.getUserFailed')
      }
    }

    return response.json()
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await fetch('/api/v2/users', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(userData),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.unauthorized')
        case 403:
          throw new Error('errors.forbidden')
        case 409:
          throw new Error('errors.accountAlreadyExists')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.createUserFailed')
      }
    }

    const data = await response.json()
    return data.user
  }

  // Admin/Owner提交創建用戶請求
  async requestUserCreate(userData: CreateUserRequest): Promise<void> {
    const response = await fetch('/api/v2/users/request', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(userData),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.unauthorized')
        case 403:
          throw new Error('errors.forbidden')
        case 409:
          throw new Error('errors.accountAlreadyExists')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.requestCreateUserFailed')
      }
    }
  }

  async deleteUser(id: string): Promise<void> {
    const response = await fetch(`/api/v2/users/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.unauthorized')
        case 403:
          throw new Error('errors.forbidden')
        case 404:
          throw new Error('errors.userNotFound')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.deleteUserFailed')
      }
    }
  }

  // Owner直接修改角色
  async updateUserRole(id: string, role: 'owner' | 'admin' | 'auditor' | 'security' | 'user'): Promise<void> {
    const response = await fetch(`/api/v2/users/${id}/role`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ role }),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.unauthorized')
        case 403:
          throw new Error('errors.forbidden')
        case 404:
          throw new Error('errors.userNotFound')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.updateUserFailed')
      }
    }
  }

  // Admin提交角色變更請求 (Owner也可以使用)
  async requestUserRoleChange(id: string, role: 'owner' | 'admin' | 'auditor' | 'security' | 'user'): Promise<void> {
    const response = await fetch(`/api/v2/users/${id}/role/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ role }),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.unauthorized')
        case 403:
          throw new Error('errors.forbidden')
        case 404:
          throw new Error('errors.userNotFound')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.requestRoleChangeFailed')
      }
    }
  }

  async resetPassword(id: string, password: string): Promise<void> {
    const response = await fetch(`/api/v2/users/${id}/reset-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ new_password: password }),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.unauthorized')
        case 403:
          throw new Error('errors.forbidden')
        case 404:
          throw new Error('errors.userNotFound')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.resetPasswordFailed')
      }
    }
  }
}

export const userService = new UserService()
export type { User, CreateUserRequest, ListUsersResponse, ListUsersParams }
